<template>
  <div>
    <Head title="Crear Descuento" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-sky-400 hover:text-sky-600" href="/discounts">Descuentos</Link>
      <span class="text-sky-400 font-medium">/</span> Crear
    </h1>
    
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="store">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <!-- Tipo de Descuento -->
          <div class="pb-8 pr-6 w-full">
            <h3 class="mb-4 font-semibold text-gray-900">Tipo de Descuento</h3>
            <ul class="items-center w-full text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg sm:flex">
              <li class="w-full border-b border-gray-200 sm:border-b-0 sm:border-r">
                <div class="flex items-center ps-3">
                  <input id="discount-type-simple" type="radio" value="simple" v-model="form.discount_type" class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500">
                  <label for="discount-type-simple" class="w-full py-3 ms-2 text-sm font-medium text-gray-900">Descuento Simple</label>
                </div>
              </li>
              <li class="w-full border-b border-gray-200 sm:border-b-0 sm:border-r">
                <div class="flex items-center ps-3">
                  <input id="discount-type-compound" type="radio" value="compound" v-model="form.discount_type" class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500">
                  <label for="discount-type-compound" class="w-full py-3 ms-2 text-sm font-medium text-gray-900">Descuento Compuesto</label>
                </div>
              </li>
            </ul>
          </div>

          <!-- Nombre del Descuento -->
          <text-input 
            v-model="form.name" 
            :error="form.errors.name" 
            class="pb-8 pr-6 w-full" 
            label="Nombre"
          />

          <!-- Período -->
          <div class="pr-6 mt-2 w-full">
            <h3 class="mb-4 font-semibold text-gray-900">Periodo</h3>
            <ul class="items-center w-full text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg sm:flex">
              <li class="w-full border-b border-gray-200 sm:border-b-0 sm:border-r">
                <div class="flex items-center ps-3">
                  <input id="checkbox-period-dates" type="radio" value="Fechas" v-model="form.period" class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500">
                  <label for="checkbox-period-dates" class="w-full py-3 ms-2 text-sm font-medium text-gray-900">Fechas</label>
                </div>
              </li>
              <li class="w-full">
                <div class="flex items-center ps-3">
                  <input id="checkbox-period-extend" type="radio" value="Indefinido" v-model="form.period" class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500">
                  <label for="checkbox-period-extend" class="w-full py-3 ms-2 text-sm font-medium text-gray-900">Indefinido</label>
                </div>
              </li>
            </ul>
          </div>

          <!-- Selector de Fechas -->
          <div v-if="form.period === 'Fechas'" class="pb-1 pr-6 w-full">
            <VueDatePicker
              v-model="form.date_range"
              :enable-time-picker="false"
              :min-date="new Date()"
              range
              model-type="dd.MM.yyyy"
              :clearable="true"
              no-today
              placeholder="Seleccionar periodo de descuento"
              input-class-name="dp-custom-input"
              teleport-center
              :error="form.errors.date_range"
            />
            <span v-if="form.errors.date_range" class="text-red-600 text-sm">{{ form.errors.date_range }}</span>
          </div>

          <div v-if="form.period === 'Indefinido'" class="pb-1 pr-6 w-full">
            <VueDatePicker
              v-model="form.date_init"
              :enable-time-picker="false"
              :min-date="new Date()"
              model-type="dd.MM.yyyy"
              :clearable="true"
              no-today
              placeholder="Selecciona una fecha de inicio"
              input-class-name="dp-custom-input"
              teleport-center
              :error="form.errors.date_range"
            />
            <span v-if="form.errors.date_range" class="text-red-600 text-sm">{{ form.errors.date_range }}</span>
          </div>

          <!-- Descuento Simple -->
          <template v-if="form.discount_type === 'simple'">
            <div class="pb-2 pr-6 mt-6 w-full">
              <select-input
                v-model="form.producto"
                :error="form.errors.producto"
                class="pb-2 w-full lg:w-1/2"
                label="Producto"
              >
                <option v-for="product in allProducts" :key="product.id" :value="product.id">
                  {{ product.producto }}
                </option>
              </select-input>

              <discount-type-selector
                v-model:type="form.type"
                v-model:condition="form.condition"
                v-model:discount="form.discount"
                :errors="form.errors"
              />
            </div>
          </template>

          <!-- Descuento Compuesto -->
          <template v-if="form.discount_type === 'compound'">
            <!-- Producto Uno -->
            <div class="pb-2 pr-6 mt-6 w-full">
              <select-input
                v-model="form.producto"
                :error="form.errors.producto"
                class="pb-2 w-full lg:w-1/2"
                label="Producto Uno"
              >
                <option v-for="product in allProducts" :key="product.id" :value="product.id">
                  {{ product.producto }}
                </option>
              </select-input>

              <discount-type-selector
                v-model:type="form.type"
                v-model:condition="form.condition"
                v-model:discount="form.discount"
                :errors="form.errors"
                suffix="uno"
              />
            </div>

            <!-- Producto Dos -->
            <div class="pb-2 pr-6 mt-4 w-full">
              <select-input
                v-model="form.producto_dos"
                :error="form.errors.producto_dos"
                class="pb-2 w-full lg:w-1/2"
                label="Producto Dos"
              >
                <option v-for="product in allProducts" :key="product.id" :value="product.id">
                  {{ product.producto }}
                </option>
              </select-input>

              <discount-type-selector
                v-model:type="form.type_dos"
                v-model:condition="form.condition_dos"
                v-model:discount="form.discount_dos"
                :errors="form.errors"
                suffix="dos"
              />
            </div>

            <div class="pb-2 pr-6 mt-4 w-full">
              <select-input
                v-model="form.producto_tres"
                :error="form.errors.producto_tres"
                class="pb-2 w-full lg:w-1/2"
                label="Producto Tres"
              >
                <option v-for="product in allProducts" :key="product.id" :value="product.id">
                  {{ product.producto }}
                </option>
              </select-input>

              <discount-type-selector
                v-model:type="form.type_tres"
                v-model:condition="form.condition_tres"
                v-model:discount="form.discount_tres"
                :errors="form.errors"
                suffix="tres"
              />
            </div>
          </template>
        </div>

        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-sky" type="submit">
            Crear Descuento
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton.vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import DiscountTypeSelector from '@/Components/DiscountTypeSelector.vue'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    TextInput,
    SelectInput,
    VueDatePicker,
    DiscountTypeSelector
  },
  layout: Layout,
  props: {
    allProducts: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        discount_type: 'simple', // Nuevo campo para determinar el tipo de descuento
        name: '',
        period: '',
        date_init: '',
        date_range: '',
        // Campos para descuento simple
        producto: '',
        type: '',
        condition: '',
        discount: '',
        // Campos para descuento compuesto        
        producto_dos: '',
        type_dos: '',
        condition_dos: '',
        discount_dos: '',

        producto_tres: '',
        type_tres: '',
        condition_tres: '',
        discount_tres: '',
      }),
    }
  },
  methods: {
    store() {
      const endpoint =  '/discounts'
      this.form.post(endpoint)
    },
  },
}
</script>